import React, { useState, useEffect } from 'react';
import { AppointmentQueueRow } from './socket.types';

interface TableProps {
    data: AppointmentQueueRow[];
    announcementsEnabled?: boolean;
}

const getStableRecordKey = (item: AppointmentQueueRow): string | null => {
    if (item.appointment_id !== undefined && item.appointment_id !== null) {
        return `appointment:${item.appointment_id}`;
    }
    if (item.queue_id !== undefined && item.queue_id !== null) {
        return `queue:${item.queue_id}`;
    }
    if (item.patient_unique_id !== undefined && item.patient_unique_id !== null) {
        return `patient:${item.patient_unique_id}`;
    }
    return null;
};

const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '';
    
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata',
        };
        
        return date.toLocaleString('en-IN', options);
    } catch {
        return dateString;
    }
};

const Table: React.FC<TableProps> = ({ data, announcementsEnabled = true }) => {
    const [updatedRows, setUpdatedRows] = useState<Set<number>>(new Set());
    const [previousData, setPreviousData] = useState<any[]>([]);
    const [audioReady, setAudioReady] = useState<boolean>(false);

    useEffect(() => {
        // Identify which rows have been updated
        const newUpdatedRows = new Set<number>();
        const tokenIdsToSpeak: string[] = [];

        const previousMap = new Map<string, AppointmentQueueRow>();
        previousData.forEach((prev) => {
            const prevKey = getStableRecordKey(prev);
            if (prevKey) previousMap.set(prevKey, prev);
        });

        data.forEach((item, index) => {
            const key = getStableRecordKey(item);
            const prevItem = key ? previousMap.get(key) : undefined;

            if (prevItem && prevItem.last_out !== item.last_out && item.last_out) {
                newUpdatedRows.add(index);
            }

            const name = item.patient_name || item.full_name || '';

            // detect newly added rows (no matching previous record) - announce token id
            if (!prevItem && previousData.length > 0) {
                const tokenId = item.appointment_id ?? item.queue_id;
                if (tokenId !== undefined && tokenId !== null) tokenIdsToSpeak.push(String(tokenId));
                return; // new row handled, no need to check for updates
            }

            // detect any field change in an existing row - announce the name
            if (prevItem && name) {
                const fieldsToWatch: (keyof typeof item)[] = [
                    'patient_name', 'full_name', 'status', 'position',
                    'mobileno', 'email', 'source', 'last_in', 'last_out', 'date',
                ];
                const hasChanged = fieldsToWatch.some(
                    (field) => String(prevItem[field] ?? '') !== String(item[field] ?? '')
                );
                if (hasChanged) {
                    const tokenId = item.appointment_id ?? item.queue_id;
                    if (tokenId !== undefined && tokenId !== null) tokenIdsToSpeak.push(String(tokenId));
                }
            }
        });

        // Always update previous data first so next diff works correctly
        setPreviousData(data);

        if (newUpdatedRows.size > 0) {
            setUpdatedRows(newUpdatedRows);

            // speak token ids on update
            if (tokenIdsToSpeak.length > 0 && announcementsEnabled) {
                const uniqueTokenIds = Array.from(new Set(tokenIdsToSpeak));
                speakTokenIds(uniqueTokenIds);
            }

            // Reset the green color after 5 seconds
            const timeout = setTimeout(() => {
                setUpdatedRows(new Set());
            }, 5000);

            return () => clearTimeout(timeout);
        }

        // speak token ids even if no 'last_out' updated rows
        if (tokenIdsToSpeak.length > 0 && announcementsEnabled) {
            const uniqueTokenIds = Array.from(new Set(tokenIdsToSpeak));
            speakTokenIds(uniqueTokenIds);
        }
    }, [data, announcementsEnabled]);

    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

        const synth = window.speechSynthesis;
        const unlockSpeech = () => {
            try {
                synth.resume();
                setAudioReady(true);
            } catch {
                return;
            }
        };

        window.addEventListener('click', unlockSpeech, { once: true });
        window.addEventListener('keydown', unlockSpeech, { once: true });
        window.addEventListener('touchstart', unlockSpeech, { once: true });

        return () => {
            window.removeEventListener('click', unlockSpeech);
            window.removeEventListener('keydown', unlockSpeech);
            window.removeEventListener('touchstart', unlockSpeech);
        };
    }, []);

    const tableStyle: React.CSSProperties = {
        width: '100%',
        borderCollapse: 'collapse',
        // stronger outer white border like waiting screens
        border: '4px solid #ffffff',
        backgroundColor: '#000000',
        color: '#ffffff',
        // slightly larger text for readability
        fontSize: '20px',
        fontWeight: 800,
    };

    const headerStyle: React.CSSProperties = {
        backgroundColor: '#ffffff',
        color: '#000000',
        padding: '18px',
        textAlign: 'left',
        // make headers visually boxed and separated by white borders
        border: '2px solid #ffffff',
        fontWeight: 'bold',
        fontSize: '18px',
    };

    const cellStyle: React.CSSProperties = {
        padding: '14px 18px',
        // use a white vertical separator for column separation + dark internal lines
        border: '1px solid #2b2b2b',
        borderRight: '2px solid #ffffff',
        textAlign: 'left',
        color: '#ffffff',
    };

    const lastOutStyle: React.CSSProperties = {
        padding: '12px 15px',
        borderBottom: '1px solid #333333',
        textAlign: 'left',
        color: '#ffffff',
    };

    const lastOutGreenStyle: React.CSSProperties = {
        padding: '12px 15px',
        borderBottom: '1px solid #333333',
        textAlign: 'left',
        color: '#00ff00',
        fontWeight: 'bold',
    };

    const rowStyle: React.CSSProperties = {
        backgroundColor: '#000000',
        color: '#ffffff',
    };

    const statusBadgeStyle = (status: string | undefined): React.CSSProperties => {
        const s = (status || '').toLowerCase().trim();
        switch (s) {
            case 'in progress':
            case 'in-progress':
            case 'inprogress':
                return {
                    display: 'inline-block',
                    backgroundColor: '#1976d2', // blue
                    color: '#ffffff',
                    padding: '6px 10px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.9em',
                };
            case 'waiting':
                return {
                    display: 'inline-block',
                    backgroundColor: '#ff9800', // orange
                    color: '#000000',
                    padding: '6px 10px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.9em',
                };
            case 'completed':
                return {
                    display: 'inline-block',
                    backgroundColor: '#388e3c', // green
                    color: '#ffffff',
                    padding: '6px 10px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.9em',
                };
            default:
                return {
                    display: 'inline-block',
                    backgroundColor: '#616161', // grey fallback
                    color: '#ffffff',
                    padding: '6px 10px',
                    borderRadius: '12px',
                    fontWeight: 700,
                    fontSize: '0.9em',
                };
        }
    };

    return (
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {!audioReady && (
                <div
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 12,
                        zIndex: 20,
                        backgroundColor: '#ffeb3b',
                        color: '#000',
                        fontWeight: 700,
                        padding: '8px 12px',
                        borderRadius: 8,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
                    }}
                >
                    Click once to enable audio announcements
                </div>
            )}
            <table style={tableStyle}>
                <thead>
                    <tr style={{ backgroundColor: '#ffffff' }}>
                        <th style={headerStyle}>Unique Appointment ID</th>
                        <th style={headerStyle}>Date Time (IST)</th>
                        <th style={headerStyle}>Patient Name</th>
                        <th style={headerStyle}>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((item, index) => (
                        // zebra shading: slightly lighter row for odd rows
                        <tr key={index} style={{ ...rowStyle, backgroundColor: index % 2 === 0 ? '#000000' : '#0a0a0a' }}>
                            <td style={{ ...cellStyle, borderLeft: '2px solid #ffffff' }}>{item.appointment_id ?? ''}</td>
                            <td style={cellStyle}>{formatDateTime(item.date)}</td>
                            <td style={cellStyle}>{item.patient_name || item.full_name}</td>
                            <td style={{ ...cellStyle, borderRight: '2px solid #ffffff' }}>
                                <span style={statusBadgeStyle(item.status)} aria-label={`status-${item.status}`}>{item.status ?? ''}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// speak token ids in order using Web Speech API (guard for availability)
// Language: Telugu (te-IN), repeated REPEAT_COUNT times each
const SPEAK_REPEAT_COUNT = 3;
const BACKEND_BASE_URL = 'https://appointments-api.quantinix.com';

function speakTokenIds(tokenIds: string[]) {
    try {
        void speakTokenIdsWithFallback(tokenIds);
    } catch (e) {
        // ignore speech errors
        // eslint-disable-next-line no-console
        console.warn('Speech error', e);
    }
}

async function speakTokenIdsWithFallback(tokenIds: string[]): Promise<void> {
    const spokenByBrowser = await tryBrowserSpeech(tokenIds);
    if (!spokenByBrowser) {
        const playedByBackend = await playBackendTts(tokenIds);
        if (!playedByBackend) {
            await speakWithAnyAvailableVoice(tokenIds);
        }
    }
}

async function tryBrowserSpeech(tokenIds: string[]): Promise<boolean> {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        return false;
    }

    const synth = window.speechSynthesis;

    try {
        synth.cancel();
        synth.resume();

        const voices = synth.getVoices();
        const teVoice = voices.find((voice) => typeof voice.lang === 'string' && voice.lang.toLowerCase().startsWith('te'));

        if (!teVoice) {
            return false;
        }

        let started = false;
        const utterances: SpeechSynthesisUtterance[] = [];

        for (const tokenId of tokenIds) {
            for (let i = 0; i < SPEAK_REPEAT_COUNT; i++) {
                const utter = new SpeechSynthesisUtterance(`టోకెన్ ఐడి ${tokenId}`);
                utter.voice = teVoice;
                utter.lang = teVoice.lang || 'te-IN';
                utter.volume = 1;
                utter.rate = 0.9;
                utter.pitch = 1;
                utter.onstart = () => {
                    started = true;
                };
                utterances.push(utter);
            }
        }

        utterances.forEach((utter) => synth.speak(utter));

        await new Promise<void>((resolve) => {
            setTimeout(() => resolve(), 1000);
        });

        return started;
    } catch {
        return false;
    }
}

async function playBackendTts(tokenIds: string[]): Promise<boolean> {
    let playedAtLeastOnce = false;

    for (const tokenId of tokenIds) {
        for (let i = 0; i < SPEAK_REPEAT_COUNT; i++) {
            const ok = await playSingleTokenAudio(tokenId);
            if (!ok) {
                return playedAtLeastOnce;
            }
            playedAtLeastOnce = true;
        }
    }

    return playedAtLeastOnce;
}

function playSingleTokenAudio(tokenId: string): Promise<boolean> {
    return new Promise((resolve) => {
        try {
            const audioUrl = `${BACKEND_BASE_URL}/api/tts?tokenId=${encodeURIComponent(tokenId)}&t=${Date.now()}`;
            const audio = new Audio(audioUrl);
            audio.preload = 'auto';

            const onEnd = () => {
                cleanup();
                resolve(true);
            };

            const onError = () => {
                cleanup();
                resolve(false);
            };

            const cleanup = () => {
                audio.onended = null;
                audio.onerror = null;
            };

            audio.onended = onEnd;
            audio.onerror = onError;

            void audio.play().catch(async () => {
                try {
                    await waitForUserInteraction();
                    void audio.play().then(() => {
                        // playback continues; onended will resolve true
                    }).catch(() => {
                        cleanup();
                        resolve(false);
                    });
                } catch {
                    cleanup();
                    resolve(false);
                }
            });
        } catch {
            resolve(false);
        }
    });
}

function waitForUserInteraction(): Promise<void> {
    return new Promise((resolve) => {
        if (typeof window === 'undefined') {
            resolve();
            return;
        }

        const done = () => {
            window.removeEventListener('click', done);
            window.removeEventListener('keydown', done);
            window.removeEventListener('touchstart', done);
            resolve();
        };

        window.addEventListener('click', done, { once: true });
        window.addEventListener('keydown', done, { once: true });
        window.addEventListener('touchstart', done, { once: true });
    });
}

async function speakWithAnyAvailableVoice(tokenIds: string[]): Promise<void> {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
        return;
    }

    const synth = window.speechSynthesis;
    synth.cancel();
    synth.resume();

    const voices = synth.getVoices();
    const fallbackVoice = voices.length > 0 ? voices[0] : undefined;

    for (const tokenId of tokenIds) {
        for (let i = 0; i < SPEAK_REPEAT_COUNT; i++) {
            const utter = new SpeechSynthesisUtterance(`Token ID ${tokenId}`);
            if (fallbackVoice) {
                utter.voice = fallbackVoice;
                utter.lang = fallbackVoice.lang || 'en-US';
            } else {
                utter.lang = 'en-US';
            }
            utter.volume = 1;
            utter.rate = 0.9;
            utter.pitch = 1;
            synth.speak(utter);
        }
    }
}

export default Table;